import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  BANNER_RANGES,
  BannerCardsStyle,
  BoxShape,
  PreviewBanner,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/bannerCardsService";
import { Pt, ShapeCanvas, area, selfIntersects } from "../TabShapeEditor";
import { GREEN, GREEN_DARK } from "../ThemeStudio/themeUtils";
import { BoxArt } from "./bannerArt";
import { DASH_GREEN } from "./BannerCardsPreview";

/** Starting polygon when the box is still a rounded rectangle. */
export const DEFAULT_BOX_SHAPE: BoxShape = {
  points: [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ],
  roundness: 0.2,
};

const CANVAS_W = 320;

/** Popup to draw the banner box outline (same editor as the tab cards). */
export const BoxShapeDialog: React.FC<{
  open: boolean;
  style: BannerCardsStyle;
  banners: PreviewBanner[];
  onClose: () => void;
  onApply: (shape: BoxShape) => void;
}> = ({ open, style, banners, onClose, onApply }) => {
  const initial = style.box.shape ?? DEFAULT_BOX_SHAPE;
  const [shape, setShape] = React.useState<BoxShape>(initial);
  const [history, setHistory] = React.useState<BoxShape[]>([]);
  const [selected, setSelected] = React.useState<number | null>(null);
  const dragStart = React.useRef<BoxShape | null>(null);

  React.useEffect(() => {
    if (open) {
      setShape(style.box.shape ?? DEFAULT_BOX_SHAPE);
      setHistory([]);
      setSelected(null);
    }
    // Only re-seed when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (next: BoxShape) => {
    setHistory((h) => [...h.slice(-30), shape]);
    setShape(next);
  };

  const onPointsChange = (points: Pt[], done: boolean) => {
    if (!done) {
      if (!dragStart.current) dragStart.current = shape;
      setShape((s) => ({ ...s, points }));
      return;
    }
    const before = dragStart.current ?? shape;
    dragStart.current = null;
    setHistory((h) => [...h.slice(-30), before]);
    setShape((s) => ({ ...s, points }));
  };

  const removeSelected = () => {
    if (selected === null || shape.points.length <= 3) return;
    commit({ ...shape, points: shape.points.filter((_, i) => i !== selected) });
    setSelected(null);
  };

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if ((e.key === "Delete" || e.key === "Backspace") && selected !== null) {
        e.preventDefault();
        removeSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const ratio = style.box.widthRatio;
  const canvasH = Math.round(CANVAS_W / ratio);
  const twisted = selfIntersects(shape.points);
  const tooSmall = area(shape.points) < 0.3;
  const previewStyle: BannerCardsStyle = { ...style, box: { ...style.box, shape } };
  const ph = style.box.height;
  const pw = Math.round(ph * ratio);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Banner box shape</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ShapeCanvas
              shape={shape}
              selected={selected}
              onSelect={setSelected}
              onChange={onPointsChange}
              width={CANVAS_W}
              height={canvasH}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Drag a point to move it · click an edge to add a point · select a point and press Delete to remove it (
              {shape.points.length}/12 points).
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, maxWidth: 380 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>Roundness</Typography>
              <Slider
                size="small"
                {...BANNER_RANGES.roundness}
                value={shape.roundness}
                onChange={(_, v) => setShape((s) => ({ ...s, roundness: v as number }))}
                onChangeCommitted={() => setHistory((h) => [...h.slice(-30), shape])}
                sx={{ color: GREEN }}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    aria-label="Undo"
                    disabled={!history.length}
                    onClick={() => {
                      setShape(history[history.length - 1]);
                      setHistory((h) => h.slice(0, -1));
                    }}
                  >
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove selected point">
                <span>
                  <IconButton
                    aria-label="Remove selected point"
                    disabled={selected === null || shape.points.length <= 3}
                    onClick={removeSelected}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                startIcon={<RestartAltIcon />}
                sx={{ textTransform: "none", color: GREEN_DARK }}
                onClick={() => {
                  commit(DEFAULT_BOX_SHAPE);
                  setSelected(null);
                }}
              >
                Start over
              </Button>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Preview</Typography>
            <Box sx={{ bgcolor: DASH_GREEN, borderRadius: 3, p: 1.5, display: "flex", gap: 1, overflowX: "auto" }}>
              {banners.slice(0, 2).map((b) => (
                <svg key={b.id} width={pw} height={ph} style={{ flexShrink: 0, overflow: "visible" }} aria-hidden>
                  <BoxArt banner={b} style={previewStyle} w={pw} h={ph} />
                </svg>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Every banner box uses this shape. In row mode the boxes stretch to fill the width.
            </Typography>
            {twisted && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Two edges cross, so the box will look twisted. Move a point to untangle it.
              </Alert>
            )}
            {tooSmall && (
              <Alert severity="error" sx={{ mt: 2 }}>
                The shape is too small to show the photo. Make it bigger to use it.
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", color: GREEN_DARK }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={tooSmall}
          onClick={() => onApply(shape)}
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none" }}
        >
          Use this shape
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoxShapeDialog;
